// FullScreen.tsx (Enhanced with your requirements)
import React, { useEffect, useRef, useState } from 'react';
import { FaTimes } from 'react-icons/fa';

interface FullScreenProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  closeButtonPosition?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  closeButtonClassName?: string;
  overlayClassName?: string;
  contentClassName?: string;
  animation?: 'fade' | 'slide' | 'zoom' | 'none';
  zIndex?: number;
  preventBodyScroll?: boolean;
  fullScreen?: boolean;
}

const FullScreen = ({
  isOpen,
  onClose,
  children,
  className = '',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  closeButtonPosition = 'top-right',
  closeButtonClassName = '',
  overlayClassName = '',
  contentClassName = '',
  animation = 'fade',
  zIndex = 9999,
  preventBodyScroll = true,
  fullScreen = false,
}: FullScreenProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen && preventBodyScroll) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
    };
  }, [isOpen, preventBodyScroll]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Animation classes
  const getAnimationClasses = () => {
    if (!isOpen) return 'opacity-0 pointer-events-none scale-95';
    
    switch (animation) {
      case 'slide':
        return 'animate-slide-up';
      case 'zoom':
        return 'animate-zoom-in';
      case 'fade':
      default:
        return 'animate-fade-in';
    }
  };

  // Close button position classes
  const getCloseButtonPosition = () => {
    const positions = {
      'top-right': 'top-4 right-4',
      'top-left': 'top-4 left-4',
      'bottom-right': 'bottom-4 right-4',
      'bottom-left': 'bottom-4 left-4',
    };
    return positions[closeButtonPosition] || positions['top-right'];
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-${zIndex} flex items-center justify-center bg-black/70 backdrop-blur-sm transition-all duration-300 ${overlayClassName}`}
      onClick={handleBackdropClick}
      style={{ zIndex }}
    >
      <div
        ref={contentRef}
        className={`
          relative bg-white 
          ${fullScreen ? 'w-full h-full max-w-full max-h-full' : 'w-full max-w-6xl max-h-[95vh] rounded-2xl overflow-hidden'}
          shadow-2xl
          ${getAnimationClasses()} 
          ${className}
        `}
        style={{ zIndex: zIndex + 1 }}
      >
        {/* Close Button */}
        {showCloseButton && (
          <button
            onClick={onClose}
            className={`
              absolute ${getCloseButtonPosition()} z-50 
              w-10 h-10 md:w-12 md:h-12 
              bg-black/50 hover:bg-black/70 backdrop-blur-sm 
              rounded-full flex items-center justify-center 
              text-white hover:text-white 
              transition-all duration-200 
              shadow-lg border border-white/20 
              hover:scale-110 
              ${closeButtonClassName}
            `}
            aria-label="Close"
          >
            <FaTimes className="text-xl md:text-2xl" />
          </button>
        )}

        {/* Content */}
        <div className={`w-full h-full overflow-auto ${contentClassName}`}>
          {children}
        </div>
      </div>

      {/* Animation Styles */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes zoom-in {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }

        .animate-slide-up {
          animation: slide-up 0.4s ease-out forwards;
        }

        .animate-zoom-in {
          animation: zoom-in 0.3s ease-out forwards;
        }

        /* Scrollbar styling */
        .overflow-auto::-webkit-scrollbar {
          width: 8px;
        }

        .overflow-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .overflow-auto::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }

        .overflow-auto::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </div>
  );
};

export default FullScreen;